// src/components/resources/tools/generators/AiChat/AiChatGenerator.tsx

'use client'

import { useRef } from 'react'
import { AiChatProvider, DeferredAiChatStateProvider } from './AiChatContext'
import { AiChatControls } from './AiChatControls'
import { AiChatPreview } from './AiChatPreview'
import type { AiProvider } from './AiChat.types'

/**
 * Props for the AI Chat Mockup Generator.
 *
 * Landing-page variants (ChatGPT, Claude, Gemini) registered in
 * `landingVariants.ts` pass `initialProvider` so the generator boots
 * pre-configured for one assistant, and `trackingSlug` so per-variant export
 * conversions are attributed to the page the user is actually on rather than
 * folded into the umbrella's counts.
 */
export interface AiChatGeneratorProps {
  initialProvider?: AiProvider
  trackingSlug?: string
}

/**
 * High-level layout that wires the AI chat provider, controls, and preview
 * together — mirroring the FakeChat shell. The `previewRef` wraps the rendered
 * surface so the Download button can capture it with html2canvas, and
 * `previewStaleRef` lets export wait for the deferred snapshot to converge.
 */
export function AiChatGenerator({ initialProvider, trackingSlug }: AiChatGeneratorProps = {}): JSX.Element {
  const previewRef = useRef<HTMLDivElement>(null)
  const previewStaleRef = useRef(false)

  return (
    <AiChatProvider initialProvider={initialProvider}>
      <div className="flex w-full max-w-7xl flex-col gap-8 px-0 pb-12 lg:min-h-screen lg:flex-row lg:items-start lg:gap-12 lg:pt-12">
        <div className="w-full lg:sticky lg:top-28 lg:w-[28rem] lg:shrink-0 xl:w-[32rem]">
          <AiChatControls previewRef={previewRef} previewStaleRef={previewStaleRef} trackingSlug={trackingSlug} />
        </div>
        <div className="min-w-0 flex-1" ref={previewRef}>
          <DeferredAiChatStateProvider staleRef={previewStaleRef}>
            <AiChatPreview />
          </DeferredAiChatStateProvider>
        </div>
      </div>
    </AiChatProvider>
  )
}
